import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString } from '../../utils/sanitization';
import { logEntityChanges } from '../../utils/logger';
import { useAuth } from '../auth/AuthContext';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';
import { generateKBArticleId } from '../../utils/documentIdUtils';

/**
 * KnowledgeBaseArticleEditor - Modal form for creating/editing Knowledge Base articles
 * 
 * Features:
 * - Create new or edit existing KB articles
 * - Lifecycle management: status (draft/published/needs-review/archived)
 * - Review scheduling with next review date
 * - Optional expiry date for auto-archiving
 * - Version notes for change tracking
 * - Update title, content, category, keywords, priority
 * - Auto-logs changes
 * 
 * Props:
 * - article: Existing KB article object (null for new article)
 * - categories: Array of category strings for the dropdown
 * - onClose: Function to call when closing modal
 * - onSaved: Function to call after successful save (with success message)
 */
const KnowledgeBaseArticleEditor = ({ article = null, categories = [], onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const isEditing = Boolean(article?.id);
  
  // Form state
  const [title, setTitle] = useState(article?.title || '');
  const [content, setContent] = useState(article?.content || '');
  const [category, setCategory] = useState(article?.category || 'general');
  const [keywords, setKeywords] = useState(article?.keywords || []);
  const [keywordsInput, setKeywordsInput] = useState((article?.keywords || []).join(', '));
  const [steps, setSteps] = useState(article?.steps || []);
  const [priority, setPriority] = useState(article?.priority || 10);
  
  // Lifecycle state
  const [status, setStatus] = useState(article?.status || (article?.isActive === false ? 'archived' : 'published'));
  const [reviewDate, setReviewDate] = useState(article?.reviewDate || '');
  const [expiresAt, setExpiresAt] = useState(article?.expiresAt || '');
  const [versionNotes, setVersionNotes] = useState('');
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Parse keywords from comma-separated input
  useEffect(() => {
    const parsedKeywords = keywordsInput
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    setKeywords(parsedKeywords);
  }, [keywordsInput]);
  
  // Add new step
  const addStep = () => {
    setSteps([...steps, '']);
  };
  
  // Update step at index
  const updateStep = (index, value) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };
  
  // Remove step at index
  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE)) return;
    
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!content.trim()) {
      setError('Content is required');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const isActive = status === 'published';
      
      const articleData = {
        title: sanitizeString(title.trim(), 200),
        content: sanitizeString(content.trim(), 5000),
        category: sanitizeString(category || 'general', 50),
        keywords: keywords.map(k => sanitizeString(k, 50)),
        steps: steps.filter(s => s.trim()).map(s => sanitizeString(s.trim(), 500)),
        priority: parseInt(priority, 10) || 10,
        status,
        isActive,
        reviewDate: reviewDate || null,
        expiresAt: expiresAt || null,
        versionNotes: versionNotes.trim() ? sanitizeString(versionNotes.trim(), 500) : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        updatedByName: currentUser.displayName || currentUser.email
      };
      
      // If marking as published or needs-review, record review info
      if (status === 'published' && article?.status !== 'published') {
        articleData.lastReviewedAt = serverTimestamp();
        articleData.lastReviewedBy = currentUser.displayName || currentUser.email;
      }
      
      if (isEditing) {
        // Update existing article
        await updateDoc(doc(db, collectionPath('chatbotKnowledge'), article.id), articleData);
        
        await logEntityChanges({
          oldObj: article,
          newObj: { ...article, ...articleData },
          fields: ['title', 'content', 'category', 'keywords', 'steps', 'priority', 'status', 'reviewDate', 'expiresAt'],
          entityType: 'KnowledgeBase',
          entityId: article.id,
          user: currentUser,
          action: 'Updated'
        });
        
        onSaved?.('Knowledge base article updated successfully');
      } else {
        // Create new article
        const docId = generateKBArticleId(title, 'manual');
        
        await setDoc(doc(db, collectionPath('chatbotKnowledge'), docId), {
          ...articleData,
          source: 'manual',
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email,
          lastReviewedAt: status === 'published' ? serverTimestamp() : null,
          lastReviewedBy: status === 'published' ? (currentUser.displayName || currentUser.email) : null
        });
        
        onSaved?.('Knowledge base article created successfully');
      }
    } catch (err) {
      console.error('Error saving knowledge article:', err);
      setError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };
  
  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700', desc: 'Not visible to chatbot or users' },
    { value: 'published', label: 'Published', color: 'bg-green-100 text-green-700', desc: 'Active and visible' },
    { value: 'needs-review', label: 'Needs Review', color: 'bg-amber-100 text-amber-700', desc: 'Flagged for admin review' },
    { value: 'archived', label: 'Archived', color: 'bg-red-100 text-red-700', desc: 'Retired, not visible' }
  ];
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Knowledge Base Article' : 'Create Knowledge Base Article'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {/* Source Badge (edit mode only) */}
          {isEditing && article && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Source:</span>
              <span className={`px-2 py-1 rounded ${
                article.source === 'faq' ? 'bg-blue-100 text-blue-700' :
                article.source === 'ai-feedback' ? 'bg-emerald-100 text-emerald-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {article.source === 'faq' ? '❓ FAQ' : article.source === 'ai-feedback' ? '🤖 AI Feedback' : '📝 Manual'}
              </span>
              <span className="text-gray-500 text-xs">ID: {article.id}</span>
            </div>
          )}
          
          {/* Lifecycle Status */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <label className="block text-sm font-semibold text-gray-800">Lifecycle Status</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {statusOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    status === opt.value
                      ? `${opt.color} border-current ring-2 ring-offset-1 ring-current`
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {statusOptions.find(o => o.value === status)?.desc}
            </p>
            
            {/* Review & Expiry Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Next Review Date
                </label>
                <input
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Expires On <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Last Reviewed Info (edit mode) */}
            {isEditing && article?.lastReviewedAt && (
              <p className="text-xs text-gray-500 pt-1">
                Last reviewed: {article.lastReviewedAt?.toDate?.()?.toLocaleDateString() || 'N/A'} by {article.lastReviewedBy || 'Unknown'}
              </p>
            )}
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Article title..."
              required
            />
          </div>
          
          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Article content..."
              required
            />
          </div>
          
          {/* Category & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="general">General</option>
                <option value="getting-started">Getting Started</option>
                <option value="customers">Customers</option>
                <option value="opportunities">Opportunities</option>
                <option value="tasks">Tasks</option>
                <option value="notes">Notes</option>
                <option value="reports">Reports</option>
                <option value="email-marketing">Email Marketing</option>
                <option value="orders">Orders</option>
                <option value="products">Products</option>
                <option value="gamification">Gamification</option>
                <option value="administration">Administration</option>
                <option value="tips">Tips & Tricks</option>
                <option value="troubleshooting">Troubleshooting</option>
                {categories
                  .filter(c => !['general', 'getting-started', 'customers', 'opportunities', 'tasks', 'notes', 'reports', 'email-marketing', 'orders', 'products', 'gamification', 'administration', 'tips', 'troubleshooting'].includes(c))
                  .map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))
                }
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10"
              />
              <p className="text-xs text-gray-500 mt-1">Higher = more relevant (1-100)</p>
            </div>
          </div>
          
          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords
            </label>
            <input
              type="text"
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="keyword1, keyword2, keyword3..."
            />
            <p className="text-xs text-gray-500 mt-1">Comma-separated keywords for search matching</p>
            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {keywords.map((kw, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Steps (optional) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Steps (optional)
              </label>
              <button
                type="button"
                onClick={addStep}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add Step
              </button>
            </div>
            <div className="space-y-2">
              {steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                  <input
                    type="text"
                    value={step}
                    onChange={(e) => updateStep(idx, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder={`Step ${idx + 1}...`}
                  />
                  <button
                    type="button"
                    onClick={() => removeStep(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {steps.length === 0 && (
                <p className="text-sm text-gray-400 italic">No steps added</p>
              )}
            </div>
          </div>
          
          {/* Version Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Version Notes <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={versionNotes}
              onChange={(e) => setVersionNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={isEditing ? 'What changed in this update...' : 'Initial creation notes...'}
            />
          </div>
        </form>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-500">
            {status === 'draft' && 'Will be saved as draft (not visible to chatbot)'}
            {status === 'published' && 'Will be immediately active in chatbot'}
            {status === 'needs-review' && 'Will be flagged for review (still visible)'}
            {status === 'archived' && 'Will be archived (not visible to chatbot)'}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Article'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseArticleEditor;
